import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PUBLIC_ID_REGEX,
  isReservedPublicIdSegment,
  JotformIntegrationIdSchema,
  PaymentLinkInvitationSchema,
} from '@am3nd-org/pointcraft-types';

import { ConnectivityProvider } from 'src/features/connectivity/connectivity-provider';
import { ConnectivityBoundary } from 'src/features/connectivity/connectivity-boundary';

/** Only guest payment pages own their connection recovery. Their proof and
 * payment APIs remain authoritative; retaining a page grants no offline access. */
export function isPublicPaymentRecoveryPath(pathname: string): boolean {
  let segments: string[];
  try {
    segments = pathname.replace(/\/+$/, '').split('/').slice(1).map(decodeURIComponent);
  } catch {
    return false;
  }
  const [first, second, third, fourth] = segments;
  // Applicant auth has no paired merchant device; this page owns its API retry UI.
  if (first?.toLowerCase() === 'register' && second?.toLowerCase() === 'onboarding') {
    return segments.length === 2;
  }
  if (first?.toLowerCase() === 'payment-link') {
    return (
      segments.length === 2 &&
      (PaymentLinkInvitationSchema.safeParse(second).success ||
        /^[1234567890ABCDEFGH]{10}$/.test(second ?? ''))
    );
  }
  if (first?.toLowerCase() === 'integrations' && second?.toLowerCase() === 'jotform') {
    if (segments.length === 3 && third?.toLowerCase() === 'widget') return true;
    return (
      segments.length === 4 &&
      JotformIntegrationIdSchema.safeParse(third).success &&
      ['pay', 'widget'].includes(fourth.toLowerCase())
    );
  }
  return Boolean(
    first &&
      PUBLIC_ID_REGEX.test(first) &&
      !isReservedPublicIdSegment(first) &&
      (segments.length === 1 || (segments.length === 2 && second.toLowerCase() === 'return'))
  );
}

/** PointPay Public only: keep the coordinator, but avoid merchant sign-in
 * recovery replacing guest payment state during initial or runtime outages. */
export function PublicPaymentConnectivityBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ConnectivityProvider>
      {isPublicPaymentRecoveryPath(pathname) ? (
        children
      ) : (
        <ConnectivityBoundary>{children}</ConnectivityBoundary>
      )}
    </ConnectivityProvider>
  );
}
